import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import type { AppNotification } from "types/Notification";
import { buildAppDeepLink } from "lib/guideBuilders";

const KEY_PERSONAL = "local_notif_ids_personal_v1";
const KEY_INVEST = "local_notif_ids_invest_v1";

function hashDedupeKey(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = Math.imul(31, h) + s.charCodeAt(i) | 0;
    }
    return Math.abs(h) % 999_983;
}

async function cancelStoredIds(storageKey: string): Promise<void> {
    if (typeof window === "undefined") return;
    let ids: number[] = [];
    try {
        const raw = window.localStorage.getItem(storageKey);
        ids = raw ? JSON.parse(raw) as number[] : [];
    } catch {
        ids = [];
    }
    if (ids.length > 0) {
        await LocalNotifications.cancel({
            notifications: ids.map((id) => ({ id })),
        });
    }
    window.localStorage.removeItem(storageKey);
}

function scheduleAtForGuideItem(item: AppNotification): Date | null {
    const d = new Date(item.date);
    if (Number.isNaN(d.getTime())) return null;
    const at = new Date(d.getTime());
    at.setHours(9, 0, 0, 0);
    if (at.getTime() <= Date.now()) return null;
    return at;
}

const PERSONAL_ID_BASE = 100_000_000;
const INVEST_ID_BASE = 200_000_000;

export interface LocalGuideNotifOptions {
    allow: boolean;
}

export async function syncPersonalLocalGuideNotifications(
    items: AppNotification[],
    opts: LocalGuideNotifOptions,
): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
        await LocalNotifications.requestPermissions();
    } catch {
        /* ignore */
    }

    await cancelStoredIds(KEY_PERSONAL);
    if (!opts.allow) return;

    const toSchedule = items.filter(
        (i) => i.surface?.includes("push") && i.category === "personal" && i.dedupeKey,
    );
    const notifications: { id: number; title: string; body: string; schedule: { at: Date }; extra: Record<string, string> }[] = [];
    const newIds: number[] = [];

    for (const item of toSchedule) {
        const at = scheduleAtForGuideItem(item);
        if (!at || !item.deepLink) continue;
        const id = PERSONAL_ID_BASE + hashDedupeKey(item.dedupeKey!);
        newIds.push(id);
        notifications.push({
            id,
            title: item.title,
            body: item.body ?? "",
            schedule: { at },
            extra: { deepLink: buildAppDeepLink(item.deepLink) },
        });
    }

    if (notifications.length > 0) {
        await LocalNotifications.schedule({ notifications });
    }
    try {
        window.localStorage.setItem(KEY_PERSONAL, JSON.stringify(newIds));
    } catch {
        /* ignore */
    }
}

export async function syncInvestmentLocalGuideNotifications(
    items: AppNotification[],
    opts: LocalGuideNotifOptions,
): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
        await LocalNotifications.requestPermissions();
    } catch {
        /* ignore */
    }

    await cancelStoredIds(KEY_INVEST);
    if (!opts.allow) return;

    const toSchedule = items.filter(
        (i) => i.surface?.includes("push")
            && i.category === "investments"
            && i.dedupeKey
            && i.sourceId?.startsWith("instrument-event:"),
    );
    const notifications: { id: number; title: string; body: string; schedule: { at: Date }; extra: Record<string, string> }[] = [];
    const newIds: number[] = [];

    for (const item of toSchedule) {
        const at = scheduleAtForGuideItem(item);
        if (!at || !item.deepLink) continue;
        const id = INVEST_ID_BASE + hashDedupeKey(item.dedupeKey!);
        newIds.push(id);
        notifications.push({
            id,
            title: item.title,
            body: item.body ?? "",
            schedule: { at },
            extra: { deepLink: buildAppDeepLink(item.deepLink) },
        });
    }

    if (notifications.length > 0) {
        await LocalNotifications.schedule({ notifications });
    }
    try {
        window.localStorage.setItem(KEY_INVEST, JSON.stringify(newIds));
    } catch {
        /* ignore */
    }
}
